<script setup lang="ts">
import { computed } from 'vue'
import { safeFormat } from '@/modules/shared/utils/date'
import { type Check } from '../types'
import { useCheckStore } from '../stores/check-store'
import StatusBadge from './StatusBadge.vue'

const props = defineProps<{
  check: Check
}>()

const store = useCheckStore()

const bgGradient = computed(() => {
  return props.check?.status === 'false_positive'
    ? 'bg-gradient-to-r from-[#3982AF] to-[#408F92]'
    : 'bg-gradient-to-r from-red-700 to-red-800'
})

const message = computed(() => {
  switch (props.check?.status) {
    case 'false_positive':
      return 'dismissed as a false positive'
    case 'confirmed_match':
      return 'confirmed as a match'
    case 'error':
    case 'error_search_retries_count_exceeded':
      return props.check?.raw_search_response?.error
  }
})

const reviewedAt = computed(() => safeFormat(props.check?.reviewed_at, 'yyyy/MM/dd'))
</script>

<template>
  <div class="rounded-lg p-3 text-sm text-white" :class="bgGradient">
    <div class="flex items-center justify-between">
      <p class="opacity-80">AML Screening Check</p>
      <span class="inline-block rounded-md bg-white">
        <StatusBadge :status="check?.status" />
      </span>
    </div>
    <div class="mt-2" v-if="store.isError(check)"><span class="font-semibold">Error:</span> {{ message }}.</div>
    <div class="mt-2" v-else>
      <span class="font-semibold">{{ store.searchParams(check)?.name }}</span> search results were {{ message }} on
      <span class="font-semibold">{{ reviewedAt }}</span> by
      <span class="font-semibold">{{ check?.reviewed_by?.name }}</span
      >.
    </div>
    <div class="mt-2 opacity-80" v-if="check?.note"><span class="font-medium">Note:</span> {{ check?.note }}</div>
  </div>
</template>
